import { useState, useEffect, useCallback, useMemo } from 'react';
import { SelectChangeEvent } from '@mui/material';
import {
  User,
  UserFilter,
  UserPaginationState,
  ApiUser,
  UserWizardData,
} from '../../../types/user.types';
import { Role, ApiRole, mapApiRoleToRole } from '../../../types/role.types';
import { usePageState } from '../../../hooks/usePageState';
import { Location, LocationsService, UsersService, RolesService } from '../../../api';
import { UsersServiceHelpers } from '../../../utils/api-helpers/UsersService.helpers';
import { mapApiUserToUser } from '../../../types/user.types';
import { USE_MOCK_USERS } from '../../../config/feature-flags';
import { MOCK_ROLES, MOCK_USERS } from '../../../utils/mockUsers';
import {
  extractErrorMessage,
  extractValidationErrors,
  mapValidationErrorsToFormFields,
  isDuplicateEmailError,
} from '../../../utils/errorHelpers';
import { USER_MESSAGES } from '../../../utils/constants';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { CreateUserDto } from '../../../types/user.types';
import type { CreatedUserPasswordInfo } from '../UserCreatedPasswordModal';

const EMPTY_LOCATION_LABEL = '—';

export const resolvePrimaryLocationName = (
  primaryLocationId: number | null | undefined,
  locationNameById: ReadonlyMap<number, string>,
): string => {
  if (primaryLocationId == null) return EMPTY_LOCATION_LABEL;
  return locationNameById.get(primaryLocationId) ?? EMPTY_LOCATION_LABEL;
};

interface UsersPageState {
  filters: UserFilter;
  showMoreFilters: boolean;
  pagination: UserPaginationState;
  selectedUsers: string[];
}

export interface UseUsersPageReturn {
  // Filter states
  filters: UserFilter;
  setFilters: React.Dispatch<React.SetStateAction<UserFilter>>;
  appliedFilters: UserFilter;
  showMoreFilters: boolean;
  setShowMoreFilters: React.Dispatch<React.SetStateAction<boolean>>;
  handleClearAppliedField: (patch: Partial<UserFilter>) => void;
  roleOptions: Role[];
  /** Active branch locations for user assignment (wizard). */
  branchLocations: Location[];
  /** O(1) lookup of any location name by id (list + detail display). */
  locationNameById: ReadonlyMap<number, string>;
  getPrimaryLocationName: (primaryLocationId?: number | null) => string;

  // Pagination states
  pagination: UserPaginationState;
  setPagination: React.Dispatch<React.SetStateAction<UserPaginationState>>;

  // Drawer states
  drawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lockedSearchTerm: string | null;
  selectedUser: User | null;
  setSelectedUser: React.Dispatch<React.SetStateAction<User | null>>;

  // Modal states
  detailModalOpen: boolean;
  setDetailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  detailUser: User | null;
  setDetailUser: React.Dispatch<React.SetStateAction<User | null>>;
  deleteModalOpen: boolean;
  setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  usersToDelete: User[];
  setUsersToDelete: React.Dispatch<React.SetStateAction<User[]>>;
  blockModalOpen: boolean;
  setBlockModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userToBlock: User | null;
  setUserToBlock: React.Dispatch<React.SetStateAction<User | null>>;

  // User data
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  selectedUsers: string[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isSearching: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;

  // Notification state
  notification: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    successMessage?: string;
    errorMessage?: string;
  };
  setNotification: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'warning' | 'info';
      successMessage?: string;
      errorMessage?: string;
    }>
  >;

  // Handlers
  handleSearch: () => void;
  handleReset: () => void;
  handleFilterChange: (newFilters: Partial<UserFilter>) => void;
  handlePaginationChange: (newPagination: Partial<UserPaginationState>) => void;
  handleItemsPerPageChange: (event: SelectChangeEvent<unknown>) => void;
  handleAddUser: () => void;
  handleEditUser: (userId: string) => void;
  handleViewUser: (userId: string) => void | Promise<void>;
  handleDeleteUsers: () => void;
  handleDeleteSingleUser: (id: string) => void;
  handleConfirmDelete: () => Promise<void>;
  handleCloseDeleteModal: () => void;
  handleBlockUser: (userId: string, isBlocked: boolean) => void;
  handleBlockUserDirect: (userId: string) => Promise<void>;
  handleUnblockUser: (userId: string) => Promise<void>;
  handleConfirmBlock: (reason?: string) => Promise<void>;
  handleCloseBlockModal: () => void;
  handleCloseDrawer: () => void;
  handleCloseDetailModal: () => void;
  handleCloseNotification: () => void;
  handleExportUsers: () => Promise<void>;
  handleSaveUser: (userData: UserWizardData) => Promise<void>;
  createdPasswordInfo: CreatedUserPasswordInfo | null;
  clearCreatedPasswordInfo: () => void;
}

const emptyUserFilters = (): UserFilter => ({
  searchTerm: '',
  status: 'all',
  roleIds: [],
  startDate: undefined,
  endDate: undefined,
  sortBy: 'updatedAt',
});

// Hook principal para la página de usuarios
export const useUsersPage = (): UseUsersPageReturn => {
  const { state, saveState } = usePageState<UsersPageState>();

  const initialFilters = state.filters || emptyUserFilters();
  const [filters, setFilters] = useState<UserFilter>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<UserFilter>(initialFilters);
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(state.showMoreFilters || false);

  const [pagination, setPagination] = useState<UserPaginationState>(
    state.pagination || {
      itemsPerPage: 10,
      currentPage: 1,
      totalItems: 0,
    },
  );

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [lockedSearchTerm, setLockedSearchTerm] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
  const [detailUser, setDetailUser] = useState<User | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [usersToDelete, setUsersToDelete] = useState<User[]>([]);
  const [blockModalOpen, setBlockModalOpen] = useState<boolean>(false);
  const [userToBlock, setUserToBlock] = useState<User | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [mockUsers, setMockUsers] = useState<User[]>(() =>
    MOCK_USERS.map((user) => ({
      ...user,
      roles: [...user.roles],
    })),
  );
  const [selectedUsers, setSelectedUsers] = useState<string[]>(state.selectedUsers || []);
  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const locationNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const location of locations) {
      map.set(location.id, location.name);
    }
    return map;
  }, [locations]);

  const branchLocations = useMemo(
    () =>
      locations.filter(
        (location) =>
          location.type === Location.type.BRANCH && location.status === Location.status.ACTIVE,
      ),
    [locations],
  );

  const getPrimaryLocationName = useCallback(
    (primaryLocationId?: number | null) =>
      resolvePrimaryLocationName(primaryLocationId, locationNameById),
    [locationNameById],
  );

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    successMessage?: string;
    errorMessage?: string;
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [createdPasswordInfo, setCreatedPasswordInfo] = useState<CreatedUserPasswordInfo | null>(
    null,
  );

  useEffect(() => {
    saveState({
      filters,
      showMoreFilters,
      pagination,
      selectedUsers,
    });
  }, [filters, showMoreFilters, pagination, selectedUsers, saveState]);

  // Initial fetch on mount
  useEffect(() => {
    setSearchTrigger(1);
  }, []);

  const fetchUsers = useCallback(async () => {
    // eslint-disable-next-line no-console
    console.log('UsersPage: fetchUsers called', {
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      filters: appliedFilters,
      USE_MOCK_USERS,
    });
    setLoading(true);
    setIsSearching(false);
    setError(null);

    if (USE_MOCK_USERS) {
      const normalizedTerm = appliedFilters.searchTerm?.trim().toLowerCase() || '';
      const roleIds = appliedFilters.roleIds || [];
      const startDate = appliedFilters.startDate ? new Date(appliedFilters.startDate) : null;
      const endDate = appliedFilters.endDate ? new Date(appliedFilters.endDate) : null;

      if (startDate) {
        startDate.setHours(0, 0, 0, 0);
      }

      if (endDate) {
        endDate.setHours(23, 59, 59, 999);
      }

      let filteredUsers = [...mockUsers];

      if (normalizedTerm) {
        filteredUsers = filteredUsers.filter((user) => {
          const values = [user.email, user.firstName, user.lastName, user.fullName, user.phone];
          return values.some((value) => value?.toLowerCase().includes(normalizedTerm));
        });
      }

      if (appliedFilters.status === 'active') {
        filteredUsers = filteredUsers.filter((user) => user.isActive);
      }

      if (appliedFilters.status === 'blocked') {
        filteredUsers = filteredUsers.filter((user) => !user.isActive);
      }

      if (roleIds.length > 0) {
        filteredUsers = filteredUsers.filter((user) =>
          user.roles.some((role) => roleIds.includes(role.id)),
        );
      }

      if (startDate) {
        filteredUsers = filteredUsers.filter(
          (user) => new Date(user.updatedAt).getTime() >= startDate.getTime(),
        );
      }

      if (endDate) {
        filteredUsers = filteredUsers.filter(
          (user) => new Date(user.updatedAt).getTime() <= endDate.getTime(),
        );
      }

      if (appliedFilters.sortBy) {
        filteredUsers.sort((a, b) => {
          switch (appliedFilters.sortBy) {
            case 'email':
              return a.email.localeCompare(b.email);
            case 'firstName':
              return a.firstName.localeCompare(b.firstName);
            case 'lastName':
              return a.lastName.localeCompare(b.lastName);
            case 'createdAt':
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            case 'updatedAt':
            default:
              return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
          }
        });
      }

      const totalItems = filteredUsers.length;
      const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
      const endIndex = startIndex + pagination.itemsPerPage;

      setUsers(filteredUsers.slice(startIndex, endIndex));
      setPagination((prev) => ({
        ...prev,
        totalItems,
      }));
      setLoading(false);
      return;
    }

    try {
      const response = await UsersServiceHelpers.usersControllerFindAllWithFilters(
        pagination.currentPage,
        pagination.itemsPerPage,
        appliedFilters,
      );

      // El backend puede devolver la respuesta en diferentes formatos
      // Intentar diferentes estructuras comunes
      let usersData: ApiUser[] = [];
      let totalItems = 0;

      if (response) {
        // Formato 1: { data: ApiUser[], meta: { total, page, limit, totalPages } }
        if (response.data && Array.isArray(response.data)) {
          usersData = response.data as ApiUser[];
          totalItems = response.meta?.total ?? usersData.length;
        }
        // Formato 2: Array directo de usuarios
        else if (Array.isArray(response)) {
          usersData = response as ApiUser[];
          totalItems = usersData.length;
        }
        // Formato 3: { users: ApiUser[], pagination: { total } }
        else if ((response as any).users && Array.isArray((response as any).users)) {
          usersData = (response as any).users as ApiUser[];
          totalItems = (response as any).pagination?.total ?? usersData.length;
        } else {
          console.warn('UsersPage: Unknown response format', response);
        }
      } else {
        console.warn('UsersPage: Empty response from API');
      }

      // Mapear usuarios del API al modelo del frontend
      const mappedUsers = usersData.map((apiUser) => mapApiUserToUser(apiUser));

      setUsers(mappedUsers);
      setPagination((prev) => ({
        ...prev,
        totalItems,
      }));
    } catch (error) {
      console.error('UsersPage: Error fetching users:', error);
      const errorMessage = extractErrorMessage(
        error,
        'No se pudieron cargar los usuarios. Intente de nuevo.',
      );
      setError(errorMessage);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.itemsPerPage, appliedFilters, mockUsers]);

  // Fetch users when search trigger, pagination, or applied filters change
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('UsersPage: useEffect triggered for fetchUsers', {
      searchTrigger,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    });
    fetchUsers();
  }, [searchTrigger, pagination.currentPage, pagination.itemsPerPage, fetchUsers]);

  const fetchRolesForFilters = useCallback(async () => {
    if (USE_MOCK_USERS) {
      setRoleOptions(MOCK_ROLES);
      return;
    }

    try {
      const response = await RolesService.rolesControllerFindAll(
        1,
        100, // Backend limit is 100
        undefined,
        undefined,
        undefined,
        undefined,
        'name',
      );

      if (response && response.data) {
        const rolesData = (response.data as ApiRole[]).map((apiRole) => mapApiRoleToRole(apiRole));
        setRoleOptions(rolesData);
      } else {
        console.warn('UsersPage: No roles data in response');
        setRoleOptions([]);
      }
    } catch (error) {
      console.error('UsersPage: Error fetching roles:', error);
      setRoleOptions([]);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    if (USE_MOCK_USERS) {
      setLocations([]);
      return;
    }

    try {
      const response = await LocationsService.locationsControllerFindAll();
      const locationsData = Array.isArray(response?.data) ? (response.data as Location[]) : [];
      setLocations(locationsData);
    } catch (error) {
      console.error('UsersPage: Error fetching locations:', error);
      setLocations([]);
    }
  }, []);

  useEffect(() => {
    fetchRolesForFilters();
  }, [fetchRolesForFilters]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleSearch = useCallback(() => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, [filters]);

  const handleReset = useCallback(() => {
    const empty = emptyUserFilters();
    setFilters(empty);
    setAppliedFilters(empty);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleClearAppliedField = useCallback((patch: Partial<UserFilter>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setAppliedFilters((prev) => ({ ...prev, ...patch }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<UserFilter>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handlePaginationChange = useCallback((newPagination: Partial<UserPaginationState>) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  }, []);

  const handleItemsPerPageChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const itemsPerPage = parseInt(event.target.value as string, 10);
      handlePaginationChange({ itemsPerPage, currentPage: 1 });
    },
    [handlePaginationChange],
  );

  const handleAddUser = useCallback(() => {
    setLockedSearchTerm(filters.searchTerm || '');
    setSelectedUser(null);
    setDrawerOpen(true);
  }, [filters.searchTerm]);

  const handleEditUser = useCallback(
    (userId: string) => {
      const user = users.find((item) => item.id === userId);
      if (user) {
        setLockedSearchTerm(filters.searchTerm || '');
        setSelectedUser(user);
        setDrawerOpen(true);
      }
    },
    [users, filters.searchTerm],
  );

  const handleViewUser = useCallback(
    async (userId: string) => {
      // Primero intentar obtener desde la lista actual
      const user = users.find((item) => item.id === userId);
      if (user) {
        setDetailUser(user);
        setDetailModalOpen(true);
        return;
      }

      // Si no está en la lista, obtener desde el backend
      if (!USE_MOCK_USERS) {
        try {
          setLoading(true);
          const response = await UsersService.usersControllerFindOne(userId);
          if (response) {
            const userData = mapApiUserToUser(response as any);
            setDetailUser(userData);
            setDetailModalOpen(true);
          }
        } catch (error) {
          console.error('Error fetching user details:', error);
          const errorMessage = extractErrorMessage(
            error,
            'Error al cargar los detalles del usuario.',
          );
          setNotification({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        } finally {
          setLoading(false);
        }
      }
    },
    [users],
  );

  const handleDeleteUsers = useCallback(() => {
    if (selectedUsers.length === 0) return;
    const usersToDeleteList = users.filter((user) => selectedUsers.includes(user.id));
    setUsersToDelete(usersToDeleteList);
    setDeleteModalOpen(true);
  }, [selectedUsers, users]);

  const handleDeleteSingleUser = useCallback(
    (id: string) => {
      const user = users.find((item) => item.id === id);
      if (!user) return;
      setUsersToDelete([user]);
      setDeleteModalOpen(true);
    },
    [users],
  );

  const handleConfirmDelete = useCallback(async () => {
    const userIds = usersToDelete.map((user) => user.id);
    if (userIds.length === 0) return;

    if (USE_MOCK_USERS) {
      setMockUsers((prev) => prev.filter((user) => !userIds.includes(user.id)));
      setSearchTrigger((prev) => prev + 1);
      setSelectedUsers([]);
      setDeleteModalOpen(false);
      setUsersToDelete([]);
      const successCount = userIds.length;
      setNotification({
        open: true,
        message:
          successCount === 1
            ? 'Usuario eliminado correctamente'
            : `${successCount} usuarios eliminados correctamente`,
        severity: 'success',
      });
      return;
    }

    try {
      const deletePromises = userIds.map((id) => UsersService.usersControllerRemove(id));
      await Promise.all(deletePromises);

      setSearchTrigger((prev) => prev + 1);
      setSelectedUsers([]);
      setDeleteModalOpen(false);
      setUsersToDelete([]);

      const successCount = userIds.length;
      setNotification({
        open: true,
        message:
          successCount === 1
            ? 'Usuario eliminado correctamente'
            : `${successCount} usuarios eliminados correctamente`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting users:', error);
      const errorMessage = extractErrorMessage(
        error,
        'No se pudieron eliminar los usuarios. Intente de nuevo.',
      );
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  }, [usersToDelete]);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setUsersToDelete([]);
  }, []);

  const handleUnblockUser = useCallback(async (userId: string) => {
    if (USE_MOCK_USERS) {
      setMockUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                isActive: true,
                blockedAt: undefined,
                blockedBy: undefined,
                blockReason: undefined,
              }
            : user,
        ),
      );
      setSearchTrigger((prev) => prev + 1);
      setNotification({
        open: true,
        message: 'Usuario desbloqueado correctamente',
        severity: 'success',
      });
      return;
    }

    try {
      await UsersServiceHelpers.usersControllerUnblock(userId);
      setSearchTrigger((prev) => prev + 1);
      setNotification({
        open: true,
        message: 'Usuario desbloqueado correctamente',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error unblocking user:', error);
      const errorMessage = extractErrorMessage(
        error,
        'No se pudo desbloquear el usuario. Intente de nuevo.',
      );
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  }, []);

  const handleBlockUser = useCallback(
    (userId: string, isBlocked: boolean) => {
      const user = users.find((u) => u.id === userId);
      if (!user) return;

      if (isBlocked) {
        // Block user - show modal
        setUserToBlock(user);
        setBlockModalOpen(true);
      } else {
        // Unblock user (direct action, no confirmation needed)
        handleUnblockUser(userId);
      }
    },
    [users, handleUnblockUser],
  );

  const handleBlockUserDirect = useCallback(async (userId: string) => {
    if (USE_MOCK_USERS) {
      setMockUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                isActive: false,
                blockedAt: new Date().toISOString(),
                blockedBy: 'Usuario Actual',
                blockReason: 'Bloqueado desde UI',
              }
            : user,
        ),
      );
      setSearchTrigger((prev) => prev + 1);
      setNotification({
        open: true,
        message: 'Usuario bloqueado correctamente',
        severity: 'success',
      });
      return;
    }

    try {
      await UsersServiceHelpers.usersControllerBlock(userId, {
        reason: undefined,
      });
      setSearchTrigger((prev) => prev + 1);
      setNotification({
        open: true,
        message: 'Usuario bloqueado correctamente',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error blocking user:', error);
      const errorMessage = extractErrorMessage(
        error,
        'No se pudo bloquear el usuario. Intente de nuevo.',
      );
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      throw error;
    }
  }, []);

  const handleConfirmBlock = useCallback(
    async (reason?: string) => {
      if (!userToBlock) return;

      if (USE_MOCK_USERS) {
        setMockUsers((prev) =>
          prev.map((user) =>
            user.id === userToBlock.id
              ? {
                  ...user,
                  isActive: false,
                  blockedAt: new Date().toISOString(),
                  blockedBy: 'Usuario Actual',
                  blockReason: reason || 'Bloqueado desde UI',
                }
              : user,
          ),
        );
        setSearchTrigger((prev) => prev + 1);
        setBlockModalOpen(false);
        setUserToBlock(null);
        setNotification({
          open: true,
          message: 'Usuario bloqueado correctamente',
          severity: 'success',
        });
        return;
      }

      try {
        await UsersServiceHelpers.usersControllerBlock(userToBlock.id, {
          reason,
        });
        setSearchTrigger((prev) => prev + 1);
        setBlockModalOpen(false);
        setUserToBlock(null);
        setNotification({
          open: true,
          message: 'Usuario bloqueado correctamente',
          severity: 'success',
        });
      } catch (error) {
        console.error('Error blocking user:', error);
        const errorMessage = extractErrorMessage(
          error,
          'No se pudo bloquear el usuario. Intente de nuevo.',
        );
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      }
    },
    [userToBlock],
  );

  const handleCloseBlockModal = useCallback(() => {
    setBlockModalOpen(false);
    setUserToBlock(null);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedUser(null);
    setLockedSearchTerm(null);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalOpen(false);
    setDetailUser(null);
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification({
      open: false,
      message: '',
      severity: 'info',
      successMessage: undefined,
      errorMessage: undefined,
    });
  }, []);

  const handleExportUsers = useCallback(async () => {
    if (USE_MOCK_USERS) {
      setNotification({
        open: true,
        message: 'Usuarios exportados correctamente',
        severity: 'success',
      });
      return;
    }

    try {
      setNotification({
        open: true,
        message: 'Exportando usuarios...',
        severity: 'info',
      });

      const response = await UsersServiceHelpers.usersControllerExportCSV();

      // El backend puede devolver el CSV de diferentes formas
      let csvContent = '';
      let filename = 'usuarios.csv';

      if (typeof response === 'string') {
        // Si es un string directo (CSV o base64)
        csvContent = response;
      } else if (response && typeof response === 'object') {
        // Si es un objeto con datos
        if ('data' in response && typeof response.data === 'string') {
          csvContent = response.data;
        } else if ('csv' in response && typeof response.csv === 'string') {
          csvContent = response.csv;
        } else if ('file' in response && typeof response.file === 'string') {
          csvContent = response.file;
        }

        // Intentar obtener el nombre del archivo
        if ('filename' in response && typeof response.filename === 'string') {
          filename = response.filename;
        }
      }

      // Si el contenido parece ser base64, decodificarlo
      if (csvContent && csvContent.startsWith('data:text/csv;base64,')) {
        const base64Data = csvContent.split(',')[1];
        csvContent = atob(base64Data);
      } else if (csvContent && /^[A-Za-z0-9+/=]+$/.test(csvContent) && csvContent.length > 100) {
        // Intentar decodificar si parece base64
        try {
          csvContent = atob(csvContent);
        } catch (e) {
          // Si falla, usar el contenido tal cual
        }
      }

      // Crear blob y descargar
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({
        open: true,
        message: 'Usuarios exportados correctamente',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error exporting users:', error);
      const errorMessage = extractErrorMessage(
        error,
        'No se pudieron exportar los usuarios. Intente de nuevo.',
      );
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  }, []);

  const handleSaveUser = useCallback(
    async (userData: UserWizardData) => {
      if (USE_MOCK_USERS) {
        const availableRoles = roleOptions.length > 0 ? roleOptions : MOCK_ROLES;
        const mappedRoles = availableRoles.filter((role) => userData.roleIds.includes(role.id));

        if (selectedUser) {
          setMockUsers((prev) =>
            prev.map((user) =>
              user.id === selectedUser.id
                ? {
                    ...user,
                    email: userData.email,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    fullName: `${userData.firstName} ${userData.lastName}`.trim(),
                    phone: userData.phone,
                    isActive: userData.isActive,
                    roles: mappedRoles,
                    primaryLocationId: userData.primaryLocationId ?? null,
                    canSellFromOtherLocations: userData.canSellFromOtherLocations ?? false,
                    updatedAt: new Date().toISOString(),
                  }
                : user,
            ),
          );
          setNotification({
            open: true,
            message: 'Usuario actualizado correctamente',
            severity: 'success',
          });
        } else {
          const newUser: User = {
            id: `mock-${Date.now()}`,
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            fullName: `${userData.firstName} ${userData.lastName}`.trim(),
            phone: userData.phone,
            isActive: userData.isActive,
            roles: mappedRoles,
            primaryLocationId: userData.primaryLocationId ?? null,
            canSellFromOtherLocations: userData.canSellFromOtherLocations ?? false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastModifiedBy: 'Usuario Actual',
          };
          setMockUsers((prev) => [newUser, ...prev]);
          setNotification({
            open: true,
            message: 'Usuario creado correctamente',
            severity: 'success',
          });
        }

        setDrawerOpen(false);
        setSelectedUser(null);
        setSearchTrigger((prev) => prev + 1);
        return;
      }

      try {
        if (selectedUser) {
          // Actualizar usuario
          const primaryLocationIdValue = userData.primaryLocationId ?? null;

          // Convert null to undefined for API compatibility (API doesn't accept null)
          const updatePayload: {
            email: string;
            firstName: string;
            lastName: string;
            phone?: string;
            roleIds: string[];
            isActive: boolean;
            primaryLocationId?: number;
            canSellFromOtherLocations: boolean;
          } = {
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            phone: userData.phone?.trim() || undefined,
            roleIds: userData.roleIds,
            isActive: userData.isActive,
            primaryLocationId: primaryLocationIdValue === null ? undefined : primaryLocationIdValue,
            canSellFromOtherLocations: userData.canSellFromOtherLocations ?? false,
          };

          await UsersService.usersControllerUpdate(selectedUser.id, updatePayload);

          setNotification({
            open: true,
            message: 'Usuario actualizado correctamente',
            severity: 'success',
          });
        } else {
          // Crear usuario (password default la asigna el backend)
          const createPayload: CreateUserDto = {
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            phone: userData.phone?.trim() || undefined,
            roleIds: userData.roleIds,
            isActive: userData.isActive,
            primaryLocationId: userData.primaryLocationId ?? undefined,
            canSellFromOtherLocations: userData.canSellFromOtherLocations ?? false,
          };

          const createResult = (await UsersService.usersControllerCreate(createPayload)) as {
            user?: { email?: string };
            temporaryPassword?: string;
          };

          const temporaryPassword = createResult?.temporaryPassword;
          if (temporaryPassword) {
            setCreatedPasswordInfo({
              email: userData.email,
              fullName: `${userData.firstName} ${userData.lastName}`.trim(),
              temporaryPassword,
            });
          }

          setNotification({
            open: true,
            message: temporaryPassword
              ? 'Usuario creado. Guarde la contraseña temporal mostrada.'
              : 'Usuario creado correctamente',
            severity: 'success',
          });
        }

        setDrawerOpen(false);
        setSelectedUser(null);
        setSearchTrigger((prev) => prev + 1);
      } catch (error) {
        console.error('Error saving user:', error);

        // Intentar extraer errores de validación
        const validationErrors = extractValidationErrors(error);
        if (validationErrors) {
          // Si hay errores de validación, mapearlos y lanzarlos como un objeto especial
          const mappedErrors = mapValidationErrorsToFormFields(validationErrors);
          // Crear un error personalizado con los errores mapeados
          const validationError = new Error('Validation failed');
          (validationError as any).validationErrors = mappedErrors;
          throw validationError;
        }

        // Si es un error de email duplicado, mostrar toast y mantener el drawer abierto
        if (isDuplicateEmailError(error)) {
          setNotification({
            open: true,
            ...USER_MESSAGES.DUPLICATE_EMAIL,
          });
          return;
        }

        // Para otros errores, mostrar notificación sin relanzar (evita overlay de runtime)
        const errorMessage = extractErrorMessage(
          error,
          selectedUser ? USER_MESSAGES.UPDATE_ERROR.message : USER_MESSAGES.CREATE_ERROR.message,
        );
        setNotification({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedUser, setDrawerOpen, setSelectedUser, setSearchTrigger, setNotification],
  );

  return {
    filters,
    setFilters,
    appliedFilters,
    showMoreFilters,
    setShowMoreFilters,
    handleClearAppliedField,
    roleOptions,
    branchLocations,
    locationNameById,
    getPrimaryLocationName,
    pagination,
    setPagination,
    drawerOpen,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setDrawerOpen,
    lockedSearchTerm,
    selectedUser,
    setSelectedUser,
    detailModalOpen,
    setDetailModalOpen,
    detailUser,
    setDetailUser,
    deleteModalOpen,
    setDeleteModalOpen,
    usersToDelete,
    setUsersToDelete,
    blockModalOpen,
    setBlockModalOpen,
    userToBlock,
    setUserToBlock,
    users,
    setUsers,
    selectedUsers,
    setSelectedUsers,
    loading,
    setLoading,
    isSearching,
    error,
    setError,
    notification,
    setNotification,
    handleSearch,
    handleReset,
    handleFilterChange,
    handlePaginationChange,
    handleItemsPerPageChange,
    handleAddUser,
    handleEditUser,
    handleViewUser,
    handleDeleteUsers,
    handleDeleteSingleUser,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleBlockUser,
    handleBlockUserDirect,
    handleUnblockUser,
    handleConfirmBlock,
    handleCloseBlockModal,
    handleCloseDrawer,
    handleCloseDetailModal,
    handleCloseNotification,
    handleExportUsers,
    handleSaveUser,
    createdPasswordInfo,
    clearCreatedPasswordInfo: () => setCreatedPasswordInfo(null),
  };
};
